import React, { useState } from 'react';
import { ArrowRight, GraduationCap, Search, Users, CheckCircle, Star, Award, BookOpen, Brain, Microscope, UserCheck, FileText, Clock, MapPin, Target, Lightbulb } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import ApplicationForm from '../components/ApplicationForm';

const PhDAdmissionsPage = () => {
  const [applyFormOpen, setApplyFormOpen] = useState(false);

  const researchAreas = [
    {
      name: "Engineering & Technology",
      fields: ["Computer Science", "Electrical Engineering", "Mechanical Engineering", "Civil Engineering"],
      universities: 150,
      avgDuration: "3-5 Years",
      funding: "Available"
    },
    {
      name: "Life Sciences & Medicine",
      fields: ["Biomedical Sciences", "Molecular Biology", "Pharmacology", "Public Health"],
      universities: 120,
      avgDuration: "4-6 Years",
      funding: "Available"
    },
    {
      name: "Physical Sciences",
      fields: ["Physics", "Chemistry", "Mathematics", "Materials Science"],
      universities: 95,
      avgDuration: "3-5 Years",
      funding: "Available"
    },
    {
      name: "Social Sciences",
      fields: ["Psychology", "Economics", "Political Science", "Sociology"],
      universities: 85,
      avgDuration: "4-6 Years",
      funding: "Available"
    },
    {
      name: "Business & Management",
      fields: ["Business Administration", "Finance", "Marketing", "Operations"],
      universities: 75,
      avgDuration: "3-4 Years",
      funding: "Limited"
    },
    {
      name: "Arts & Humanities",
      fields: ["Literature", "History", "Philosophy", "Linguistics"],
      universities: 65,
      avgDuration: "4-7 Years",
      funding: "Competitive"
    }
  ];

  const services = [
    {
      icon: Search,
      title: "Research Program Identification",
      description: "Personalized guidance in identifying suitable research programs that align with your interests and career goals"
    },
    {
      icon: Users,
      title: "Supervisor Matching",
      description: "Connect with potential supervisors and research mentors at leading international universities"
    },
    {
      icon: FileText,
      title: "Application Preparation",
      description: "Comprehensive support in preparing strong applications including research proposals and statements"
    },
    {
      icon: Award,
      title: "Funding Assistance",
      description: "Guidance on scholarships, fellowships, and funding opportunities for PhD research"
    },
    {
      icon: Brain,
      title: "Research Proposal Development",
      description: "Expert assistance in crafting compelling research proposals that stand out to admissions committees"
    },
    {
      icon: UserCheck,
      title: "Interview Preparation",
      description: "Mock interviews and preparation for academic interviews and research presentations"
    }
  ];

  const process = [
    {
      step: "01",
      title: "Initial Consultation",
      description: "Comprehensive assessment of your research interests, academic background, and career aspirations"
    },
    {
      step: "02",
      title: "Program & University Selection",
      description: "Identify suitable PhD programs and universities that match your research goals and preferences"
    },
    {
      step: "03",
      title: "Supervisor Identification",
      description: "Research and connect with potential supervisors whose work aligns with your research interests"
    },
    {
      step: "04",
      title: "Application Development",
      description: "Prepare comprehensive applications including research proposals, personal statements, and CVs"
    },
    {
      step: "05",
      title: "Funding Applications",
      description: "Apply for scholarships, fellowships, and other funding opportunities to support your research"
    },
    {
      step: "06",
      title: "Interview & Final Steps",
      description: "Interview preparation and support through final admission and enrollment processes"
    }
  ];

  const countries = [
    { name: "United States", universities: "200+", funding: "Excellent", duration: "4-6 Years" },
    { name: "United Kingdom", universities: "150+", funding: "Good", duration: "3-4 Years" },
    { name: "Canada", universities: "80+", funding: "Excellent", duration: "4-6 Years" },
    { name: "Australia", universities: "60+", funding: "Good", duration: "3-4 Years" },
    { name: "Germany", universities: "120+", funding: "Excellent", duration: "3-5 Years" },
    { name: "Netherlands", universities: "40+", funding: "Good", duration: "4 Years" }
  ];

  const stats = [
    { value: "500+", label: "PhD Placements" },
    { value: "200+", label: "Partner Universities" },
    { value: "25+", label: "Countries" },
    { value: "95%", label: "Success Rate" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-24 pb-20 xl:pb-32 bg-gradient-to-br from-navy-50 via-white to-brand-50 relative overflow-hidden">
        <div className="max-w-7xl xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 xl:gap-16 items-center">
            
            {/* Left Content */}
            <div>
              <div className="inline-block px-6 py-2 bg-navy-100 text-brand-600 rounded-full text-sm xl:text-base font-medium mb-6 xl:mb-8">
                Advanced Research Education
              </div>
              
              <h1 className="text-5xl md:text-6xl xl:text-7xl font-bold text-gray-900 mb-6 xl:mb-8 leading-tight">
                PhD
                <span className="text-brand-600 block">Admissions</span>
              </h1>
              
              <p className="text-xl xl:text-2xl text-gray-600 mb-8 xl:mb-12 leading-relaxed">
                We assist aspiring researchers in pursuing PhD opportunities at leading international universities. Our team provides personalized guidance in identifying suitable research programs, connecting with potential supervisors, and preparing strong applications.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 xl:gap-8 mb-12 xl:mb-16">
                <Button 
                  onClick={() => setApplyFormOpen(true)}
                  className="px-8 xl:px-12 py-4 xl:py-6 bg-navy-900 hover:bg-navy-800 text-white text-lg xl:text-xl font-semibold rounded-lg shadow-lg"
                >
                  Start Your PhD Journey
                  <ArrowRight className="w-5 h-5 xl:w-6 xl:h-6 ml-2" />
                </Button>
                
                <Button 
                  variant="outline"
                  className="px-8 xl:px-12 py-4 xl:py-6 border-2 border-navy-900 text-brand-600 hover:bg-navy-50 text-lg xl:text-xl font-semibold rounded-lg"
                >
                  Research Programs
                </Button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 xl:gap-8">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-2xl xl:text-3xl font-bold text-brand-600 mb-1">{stat.value}</div>
                    <div className="text-sm xl:text-base text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Image */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                  alt="PhD Research"
                  className="w-full h-[500px] xl:h-[600px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-950/40 to-transparent"></div>
              </div>
              
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-6 shadow-xl border border-gray-100">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-navy-100 rounded-full flex items-center justify-center">
                    <Brain className="w-6 h-6 text-brand-600" />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-gray-900">Research Excellence</div>
                    <div className="text-sm text-gray-600">Global Recognition</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 xl:py-32 bg-white">
        <div className="max-w-7xl xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 xl:gap-16 items-center">
            
            {/* Left Image */}
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1532094349884-543bc11b234d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Research Laboratory"
                className="w-full h-[400px] xl:h-[500px] object-cover rounded-2xl shadow-lg"
              />
            </div>

            {/* Right Content */}
            <div>
              <h2 className="text-4xl xl:text-5xl font-bold text-gray-900 mb-6 xl:mb-8">
                Your Research Career Starts Here
              </h2>
              
              <p className="text-lg xl:text-xl text-gray-600 mb-6 xl:mb-8 leading-relaxed">
                We support students throughout the admission process to help them take the next step in their academic and research careers. Our comprehensive approach ensures you're well-prepared for the challenges and opportunities of doctoral research.
              </p>
              
              <p className="text-lg xl:text-xl text-gray-600 mb-8 xl:mb-10 leading-relaxed">
                From identifying the right research programs to connecting with world-renowned supervisors, we provide the expertise and support you need to succeed in your PhD journey.
              </p>

              <div className="space-y-4 xl:space-y-6">
                <div className="flex items-center">
                  <CheckCircle className="w-6 h-6 xl:w-7 xl:h-7 text-brand-500 mr-4" />
                  <span className="text-lg xl:text-xl text-gray-700">Personalized research program matching</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-6 h-6 xl:w-7 xl:h-7 text-brand-500 mr-4" />
                  <span className="text-lg xl:text-xl text-gray-700">Expert supervisor connections</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-6 h-6 xl:w-7 xl:h-7 text-brand-500 mr-4" />
                  <span className="text-lg xl:text-xl text-gray-700">Comprehensive application support</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 xl:py-32 bg-gray-50">
        <div className="max-w-7xl xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="text-center mb-16 xl:mb-20">
            <h2 className="text-4xl xl:text-5xl font-bold text-gray-900 mb-6 xl:mb-8">
              Our PhD Admission Services
            </h2>
            <p className="text-xl xl:text-2xl text-gray-600 max-w-3xl xl:max-w-4xl mx-auto">
              Comprehensive support for every step of your PhD application journey
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 xl:gap-12">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl p-8 xl:p-10 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="w-16 h-16 xl:w-20 xl:h-20 bg-navy-100 rounded-xl flex items-center justify-center mb-6 xl:mb-8">
                  <service.icon className="w-8 h-8 xl:w-10 xl:h-10 text-brand-600" />
                </div>
                <h3 className="text-xl xl:text-2xl font-bold text-gray-900 mb-4 xl:mb-6">{service.title}</h3>
                <p className="text-gray-600 xl:text-lg leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Research Areas Section */}
      <section className="py-20 xl:py-32 bg-white">
        <div className="max-w-7xl xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="text-center mb-16 xl:mb-20">
            <h2 className="text-4xl xl:text-5xl font-bold text-gray-900 mb-6 xl:mb-8">
              Research Areas & Disciplines
            </h2>
            <p className="text-xl xl:text-2xl text-gray-600 max-w-3xl xl:max-w-4xl mx-auto">
              Explore PhD opportunities across diverse fields of study
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 xl:gap-12">
            {researchAreas.map((area, index) => (
              <div key={index} className="bg-gradient-to-br from-navy-50 to-brand-50 rounded-xl p-8 xl:p-10 border border-gray-200 hover:shadow-lg transition-all duration-300">
                <div className="flex items-center justify-between mb-6 xl:mb-8">
                  <h3 className="text-xl xl:text-2xl font-bold text-gray-900">{area.name}</h3>
                  <Microscope className="w-6 h-6 xl:w-8 xl:h-8 text-brand-600" />
                </div>
                
                <div className="space-y-4 xl:space-y-6 mb-6 xl:mb-8">
                  <div>
                    <span className="text-sm xl:text-base text-gray-600 font-medium">Key Fields:</span>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {area.fields.map((field, idx) => (
                        <span key={idx} className="px-3 py-1 bg-white rounded-full text-xs xl:text-sm text-gray-700 border border-gray-200">
                          {field}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm xl:text-base text-gray-600">Universities:</span>
                      <div className="font-semibold text-gray-900 xl:text-lg">{area.universities}</div>
                    </div>
                    <div>
                      <span className="text-sm xl:text-base text-gray-600">Duration:</span>
                      <div className="font-semibold text-gray-900 xl:text-lg">{area.avgDuration}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <CheckCircle className="w-4 h-4 xl:w-5 xl:h-5 text-brand-500 mr-2" />
                    <span className="text-sm xl:text-base text-gray-600">Funding: {area.funding}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 xl:py-32 bg-gray-50">
        <div className="max-w-7xl xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="text-center mb-16 xl:mb-20">
            <h2 className="text-4xl xl:text-5xl font-bold text-gray-900 mb-6 xl:mb-8">
              Our PhD Admission Process
            </h2>
            <p className="text-xl xl:text-2xl text-gray-600 max-w-3xl xl:max-w-4xl mx-auto">
              A structured approach to securing your PhD admission
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 xl:gap-12">
            {process.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-xl p-8 xl:p-10 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="w-16 h-16 xl:w-20 xl:h-20 bg-navy-900 rounded-full flex items-center justify-center mb-6 xl:mb-8">
                    <span className="text-2xl xl:text-3xl font-bold text-white">{step.step}</span>
                  </div>
                  <h3 className="text-xl xl:text-2xl font-bold text-gray-900 mb-4 xl:mb-6">{step.title}</h3>
                  <p className="text-gray-600 xl:text-lg leading-relaxed">{step.description}</p>
                </div>
                
                {/* Connector Line */}
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-6 xl:-right-8 w-12 xl:w-16 h-0.5 bg-navy-200"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Countries Section */}
      <section className="py-20 xl:py-32 bg-white">
        <div className="max-w-7xl xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="text-center mb-16 xl:mb-20">
            <h2 className="text-4xl xl:text-5xl font-bold text-gray-900 mb-6 xl:mb-8">
              Top PhD Destinations
            </h2>
            <p className="text-xl xl:text-2xl text-gray-600 max-w-3xl xl:max-w-4xl mx-auto">
              Leading countries for doctoral research and education
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 xl:gap-12">
            {countries.map((country, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-navy-50 rounded-xl p-8 xl:p-10 border border-gray-200 hover:shadow-lg transition-all duration-300">
                <div className="flex items-center justify-between mb-6 xl:mb-8">
                  <h3 className="text-2xl xl:text-3xl font-bold text-gray-900">{country.name}</h3>
                  <MapPin className="w-6 h-6 xl:w-8 xl:h-8 text-brand-600" />
                </div>
                
                <div className="space-y-4 xl:space-y-6">
                  <div className="flex justify-between">
                    <span className="text-gray-600 xl:text-lg">Universities:</span>
                    <span className="font-semibold text-gray-900 xl:text-lg">{country.universities}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 xl:text-lg">Funding:</span>
                    <span className="font-semibold text-gray-900 xl:text-lg">{country.funding}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 xl:text-lg">Duration:</span>
                    <span className="font-semibold text-gray-900 xl:text-lg">{country.duration}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 xl:py-32 bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1581092795360-fd1ca04f0952?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
            alt="Research Team"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        
        <div className="relative z-10 max-w-4xl xl:max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 text-center">
          <h2 className="text-4xl xl:text-5xl font-bold text-white mb-6 xl:mb-8">
            Ready to Begin Your Research Journey?
          </h2>
          <p className="text-xl xl:text-2xl text-navy-100 mb-12 xl:mb-16 max-w-3xl xl:max-w-4xl mx-auto">
            Take the first step towards your PhD and join the next generation of researchers making groundbreaking discoveries.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 xl:gap-8 justify-center">
            <Button 
              onClick={() => setApplyFormOpen(true)}
              className="px-12 xl:px-16 py-4 xl:py-6 bg-white text-brand-600 hover:bg-gray-100 text-lg xl:text-xl font-semibold rounded-lg shadow-lg"
            >
              Start Application
              <ArrowRight className="w-5 h-5 xl:w-6 xl:h-6 ml-2" />
            </Button>
            
            <Button 
              variant="outline" 
              className="px-12 xl:px-16 py-4 xl:py-6 border-2 border-white text-white hover:bg-white hover:text-brand-600 text-lg xl:text-xl font-semibold rounded-lg"
            >
              Schedule Consultation
            </Button>
          </div>

          <div className="mt-12 xl:mt-16 flex flex-wrap justify-center items-center gap-8 xl:gap-12 text-navy-100">
            <div className="flex items-center">
              <Award className="w-5 h-5 xl:w-6 xl:h-6 mr-2" />
              <span className="xl:text-lg">Top Universities</span>
            </div>
            <div className="flex items-center">
              <Lightbulb className="w-5 h-5 xl:w-6 xl:h-6 mr-2" />
              <span className="xl:text-lg">Research Excellence</span>
            </div>
            <div className="flex items-center">
              <Users className="w-5 h-5 xl:w-6 xl:h-6 mr-2" />
              <span className="xl:text-lg">Expert Guidance</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
      
      {/* Application Form Modal */}
      <ApplicationForm open={applyFormOpen} onOpenChange={setApplyFormOpen} />
    </div>
  );
};

export default PhDAdmissionsPage;